import { useCallback, useEffect, useRef } from 'react';

type DebouncedFunction<TArgs extends readonly unknown[], TReturn> = {
  (...args: TArgs): void;
  cancel: () => void;
  /**
   * If a call is pending, invokes it immediately with its original arguments and
   * returns its result (skipping the debounce timer). Returns undefined if
   * nothing is pending.
   */
  flush: () => TReturn | undefined;
};

export function useDebounce<TArgs extends readonly unknown[], TReturn = void>(
  callback: (...args: TArgs) => TReturn,
  delay: number,
  options?: { flushOnUnmount?: boolean }
): DebouncedFunction<TArgs, TReturn> {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingArgsRef = useRef<TArgs | null>(null);
  const callbackRef = useRef(callback);
  callbackRef.current = callback;
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    pendingArgsRef.current = null;
  }, []);

  const flush = useCallback((): TReturn | undefined => {
    if (!timeoutRef.current || !pendingArgsRef.current) return undefined;
    const args = pendingArgsRef.current;
    cancel();
    return callbackRef.current(...args);
  }, [cancel]);

  const debouncedCallback = useCallback(
    (...args: TArgs) => {
      cancel();
      pendingArgsRef.current = args;

      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        pendingArgsRef.current = null;
        callbackRef.current(...args);
      }, delay);
    },
    [delay, cancel]
  );

  useEffect(() => {
    return () => {
      if (optionsRef.current?.flushOnUnmount) {
        flush();
      } else {
        cancel();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- unmount-only; flush/cancel are stable
  }, []);

  // Create a callable function with cancel/flush methods
  const result = Object.assign(debouncedCallback, { cancel, flush });

  return result;
}
